import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import { AMOUNT_INPUT_VALUE_TYPE, COMMON_CURRENCY_PRECISION, CRYPTO_CURRENCY_PRECISION } from 'components/constants';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { useApplicationContext } from 'contexts/ApplicationContext';
import { getAllCryptoCurrencies, isCryptoCurrency, isPositiveNumber } from 'services/utils';
import { getCryptoNetworks, isCryptoWithNetworks, getDefaultNetwork } from 'services/cryptoNetworks';
import AmountInput from 'uikit/AmountInput';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './PopUpCreateNewCurrencyPairScheme.scss';

const PopUpCreateNewCurrencyPairScheme = ({
  isLoading,
  handleClose,
  handleCreateNewCurrencyPair,
  isUpdate,
  currencyPairData
}) => {
  const i18n = useContext(i18nContext);
  const { constants } = useApplicationContext();
  const allCryptoCurrencies = getAllCryptoCurrencies(constants);

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      from: isUpdate ? currencyPairData.currencyFrom || currencyPairData.from : '',
      fromNetwork: isUpdate ? currencyPairData.currencyFromNetwork || currencyPairData.fromNetwork || '' : '',
      to: isUpdate ? currencyPairData.currencyTo || currencyPairData.to : '',
      toNetwork: isUpdate ? currencyPairData.currencyToNetwork || currencyPairData.toNetwork || '' : '',
      manualMinAmount: isUpdate ? currencyPairData.manualMinAmount : '0',
      active: isUpdate ? currencyPairData.active : true,
      manual: isUpdate ? currencyPairData.manual : false
    },
    validationSchema: Yup.object().shape({
      from: Yup.string()
        .required(i18n.getMessage('currencyPairs.createNewPair.modal.validation.isRequired'))
        .max(50, i18n.getMessage('currencyPairs.createNewPair.modal.validation.maxSize', { size: 50 })),
      to: Yup.string()
        .required(i18n.getMessage('currencyPairs.createNewPair.modal.validation.isRequired'))
        .max(50, i18n.getMessage('currencyPairs.createNewPair.modal.validation.maxSize', { size: 50 }))
        .test(
          'different-pair',
          i18n.getMessage('currencyPairs.createNewPair.modal.validation.mustBeDifferent'),
          function (value) {
            const { from, fromNetwork, toNetwork } = this.parent;
            // Same currency is allowed only if networks are different
            if (value === from) {
              return fromNetwork !== toNetwork;
            }
            return true;
          }
        ),
      fromNetwork: Yup.string().test(
        'required-for-crypto',
        i18n.getMessage('currencyPairs.createNewPair.modal.validation.isRequired'),
        function (value) {
          const { from } = this.parent;
          if (isCryptoWithNetworks(from, constants)) {
            return !!value;
          }
          return true;
        }
      ),
      toNetwork: Yup.string().test(
        'required-for-crypto',
        i18n.getMessage('currencyPairs.createNewPair.modal.validation.isRequired'),
        function (value) {
          const { to } = this.parent;
          if (isCryptoWithNetworks(to, constants)) {
            return !!value;
          }
          return true;
        }
      ),
      manualMinAmount: Yup.string()
        .required(i18n.getMessage('addNewTariffForTariffGroup.modal.validation.isRequired'))
        .test(
          'is-positive',
          i18n.getMessage('addNewTariffForTariffGroup.modal.validation.isPositiveNumber', { min: 0 }),
          (value) => isPositiveNumber(value)
        )
    }),
    onSubmit: async (values) => {
      // Transform field names to match API expectations
      const apiData = {
        currencyFrom: values.from,
        currencyFromNetwork: values.fromNetwork || '',
        currencyTo: values.to,
        currencyToNetwork: values.toNetwork || '',
        active: values.active,
        manual: values.manual,
        manualMinAmount: values.manualMinAmount
      };

      // For update, add the id
      if (isUpdate && currencyPairData?.id) {
        apiData.id = currencyPairData.id;
      }

      await handleCreateNewCurrencyPair(apiData);
    }
  });

  const { values, handleChange, handleSubmit, errors, setFieldValue } = form;

  // Handle network selection when currency changes
  useEffect(() => {
    if (!isUpdate) {
      const networks = getCryptoNetworks(values.from, constants);
      if (networks.length === 1) {
        // Auto-select if only one network available
        setFieldValue('fromNetwork', networks[0]);
      } else if (networks.length === 0 || !values.from) {
        // Clear network if no networks available or no currency selected
        setFieldValue('fromNetwork', '');
      } else {
        // Clear network when currency changes (multiple networks available)
        setFieldValue('fromNetwork', '');
      }
    }
  }, [values.from, constants, setFieldValue, isUpdate]);

  useEffect(() => {
    if (!isUpdate) {
      const networks = getCryptoNetworks(values.to, constants);
      if (networks.length === 1) {
        // Auto-select if only one network available
        setFieldValue('toNetwork', networks[0]);
      } else if (networks.length === 0 || !values.to) {
        // Clear network if no networks available or no currency selected
        setFieldValue('toNetwork', '');
      } else {
        // Clear network when currency changes (multiple networks available)
        setFieldValue('toNetwork', '');
      }
    }
  }, [values.to, constants, setFieldValue, isUpdate]);

  const currencyOptions = constants?.currencies?.map((currency) => {
    return {
      key: currency,
      value: currency
    };
  });

  if (!constants) {
    return <Loader className={'application-loader scheme-add-new-markup-container'} />;
  }

  return (
    <div className={'scheme-wrapper scheme-add-new-markup-container'}>
      <div className={'scheme-add-new-markup-container-header'}>
        <h3>
          {isUpdate
            ? i18n.getMessage('currencyPairs.createNewPair.modal.title.update')
            : i18n.getMessage('currencyPairs.createNewPair.modal.title.create')}
        </h3>
        <CloseIcon className={'scheme-add-new-markup-container-header-close-icon'} onClick={handleClose} />
      </div>
      <form className={'scheme-add-new-markup-form'} onSubmit={handleSubmit}>
        <div className={'scheme-add-new-markup-form-inputs-wrapper'}>
          <div className={'scheme-add-new-markup-form-currency-with-network'}>
            <InputDropDownSearch
              className={'scheme-add-new-markup-input-dropdown-search'}
              id={'from'}
              name={'from'}
              label={i18n.getMessage('currencyPairs.createNewPair.modal.input.from')}
              options={currencyOptions}
              value={values.from || ''}
              onChange={(name, value) => setFieldValue(name, value)}
              isDisabled={isLoading || isUpdate}
              error={errors?.from}
            />

            {isCryptoWithNetworks(values.from, constants) && (
              <InputDropDownSearch
                className={'scheme-add-new-markup-input-dropdown-search'}
                id={'fromNetwork'}
                name={'fromNetwork'}
                label={i18n.getMessage('currencyPairs.createNewPair.modal.input.fromNetwork')}
                options={getCryptoNetworks(values.from, constants).map((network) => ({ key: network, value: network }))}
                value={values.fromNetwork || ''}
                onChange={(name, value) => setFieldValue(name, value)}
                isDisabled={isLoading || isUpdate || !values.from}
                error={errors?.fromNetwork}
              />
            )}
          </div>

          <AmountInput
            className={'scheme-add-new-markup-amount-input'}
            id={'manualMinAmount'}
            name={'manualMinAmount'}
            label={i18n.getMessage('currencyPairs.createNewPair.modal.input.manualMinAmount')}
            returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
            value={values.manualMinAmount}
            onChange={handleChange}
            error={errors?.manualMinAmount}
            precision={
              isCryptoCurrency(values.from, allCryptoCurrencies) ? CRYPTO_CURRENCY_PRECISION : COMMON_CURRENCY_PRECISION
            }
          />

          <div className={'scheme-add-new-markup-form-currency-with-network'}>
            <InputDropDownSearch
              className={'scheme-add-new-markup-input-dropdown-search'}
              id={'to'}
              name={'to'}
              label={i18n.getMessage('currencyPairs.createNewPair.modal.input.to')}
              options={currencyOptions}
              value={values.to || ''}
              onChange={(name, value) => setFieldValue(name, value)}
              isDisabled={isLoading || isUpdate}
              error={errors?.to}
            />

            {isCryptoWithNetworks(values.to, constants) && (
              <InputDropDownSearch
                className={'scheme-add-new-markup-input-dropdown-search'}
                id={'toNetwork'}
                name={'toNetwork'}
                label={i18n.getMessage('currencyPairs.createNewPair.modal.input.toNetwork')}
                options={getCryptoNetworks(values.to, constants).map((network) => ({ key: network, value: network }))}
                value={values.toNetwork || ''}
                onChange={(name, value) => setFieldValue(name, value)}
                isDisabled={isLoading || isUpdate || !values.to}
                error={errors?.toNetwork}
              />
            )}
          </div>

          <div className={'scheme-add-new-markup-settings-wrapper'}>
            <CheckBox
              className={'scheme-add-new-markup-checkbox'}
              name={'active'}
              isActive={values.active}
              isReverseView={true}
              showCheckBox={true}
              onChange={() => setFieldValue('active', !values.active)}
              value={i18n.getMessage('currencyPairs.table.rowContent.status.active')}
              disabled={isLoading}
            />
          </div>

          <div className={'scheme-add-new-markup-settings-wrapper'}>
            <CheckBox
              className={'scheme-add-new-markup-checkbox'}
              name={'manual'}
              isActive={values.manual}
              isReverseView={true}
              showCheckBox={true}
              onChange={() => setFieldValue('manual', !values.manual)}
              value={i18n.getMessage('currencyPairs.table.rowContent.manual')}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className={'scheme-add-new-markup-actions-wrapper'}>
          <Button
            className={'scheme-add-new-markup-button'}
            type={'outline'}
            onClick={handleClose}
            isDisabled={isLoading}
          >
            {i18n.getMessage('currencyPairs.createNewPair.modal.button.cancel')}
          </Button>
          <Button
            className={'scheme-add-new-markup-button'}
            roleType={'submit'}
            type={'primary'}
            isDisabled={isLoading}
          >
            {isUpdate
              ? i18n.getMessage('currencyPairs.createNewPair.modal.button.update')
              : i18n.getMessage('currencyPairs.createNewPair.modal.button.add')}
          </Button>
        </div>
      </form>
    </div>
  );
};

PopUpCreateNewCurrencyPairScheme.propTypes = {
  isLoading: PropTypes.bool,
  handleClose: PropTypes.func,
  handleCreateNewCurrencyPair: PropTypes.func,
  isUpdate: PropTypes.bool,
  currencyPairData: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])])
};

export default PopUpCreateNewCurrencyPairScheme;
